import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  clearAccessContextStorage,
  fetchAndPersistAccessContext,
  getStoredAccessContext,
} from '../utils/accessControl';
import { clearAuthRedirectThrottle } from '../api/axiosInterceptors';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [accessContext, setAccessContext] = useState(() => getStoredAccessContext());
  const [isLoggedIn, setIsLoggedIn] = useState(() => Boolean(localStorage.getItem('token')));
  const [isAuthReady, setIsAuthReady] = useState(() => !localStorage.getItem('token'));

  useEffect(() => {
    const handleAuthCleared = () => {
      setAccessContext(null);
      setIsLoggedIn(false);
      setIsAuthReady(true);
    };

    window.addEventListener('smarttile:auth-cleared', handleAuthCleared);
    return () => {
      window.removeEventListener('smarttile:auth-cleared', handleAuthCleared);
    };
  }, []);

  const refreshAccessContext = useCallback(async (tokenOverride, emailOverride) => {
    const token = tokenOverride || localStorage.getItem('token');

    if (!token) {
      setAccessContext(null);
      setIsLoggedIn(false);
      setIsAuthReady(true);
      return null;
    }

    const context = await fetchAndPersistAccessContext(token, emailOverride || localStorage.getItem('userEmail'));
    setAccessContext(context);
    setIsLoggedIn(true);
    setIsAuthReady(true);
    return context;
  }, []);

  useEffect(() => {
    let disposed = false;

    const bootstrap = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        if (!disposed) {
          setAccessContext(null);
          setIsLoggedIn(false);
          setIsAuthReady(true);
        }
        return;
      }

      if (!disposed) {
        setIsAuthReady(false);
      }

      try {
        const context = await fetchAndPersistAccessContext(token, localStorage.getItem('userEmail'));
        if (disposed) return;
        setAccessContext(context);
        setIsLoggedIn(true);
      } catch (_) {
        if (disposed) return;
        localStorage.removeItem('token');
        clearAccessContextStorage();
        setAccessContext(null);
        setIsLoggedIn(false);
      } finally {
        if (!disposed) {
          setIsAuthReady(true);
        }
      }
    };

    bootstrap();

    return () => {
      disposed = true;
    };
  }, []);

  const login = useCallback(async (token, email) => {
    clearAuthRedirectThrottle();
    localStorage.setItem('token', token);
    if (email) {
      localStorage.setItem('userEmail', String(email));
    }

    setIsLoggedIn(true);
    setIsAuthReady(false);

    try {
      return await refreshAccessContext(token, email);
    } catch (error) {
      localStorage.removeItem('token');
      clearAccessContextStorage();
      setAccessContext(null);
      setIsLoggedIn(false);
      setIsAuthReady(true);
      throw error;
    }
  }, [refreshAccessContext]);

  const logout = useCallback(async () => {
    clearAuthRedirectThrottle();
    localStorage.removeItem('token');
    clearAccessContextStorage();
    setAccessContext(null);
    setIsLoggedIn(false);
    setIsAuthReady(true);
  }, []);

  const value = useMemo(() => ({
    accessContext,
    isAuthReady,
    isLoggedIn,
    login,
    logout,
    refreshAccessContext,
  }), [accessContext, isAuthReady, isLoggedIn, login, logout, refreshAccessContext]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
